import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus as PlusIcon, Trash2 as TrashIcon } from 'lucide-react';
import { listUsuariosDeEmpresa } from '@lib/services/empresas.service';
import {
  asignarEmpresaAUsuario,
  listUsuarios,
  quitarEmpresaDeUsuario,
} from '@lib/services/usuarios.service';
import { UserRole } from '@lib/types';
import { useCanMutate } from '@lib/hooks/useCanMutate';

/**
 * Usuarios asignados a una organización. Sale del modal de Empresas para poder
 * vivir como subsección de `/empresas/:id`, donde hay lugar para la tabla y
 * para el selector de "agregar" sin apilar dos capas de modal.
 */
export function UsuariosDeEmpresa({ empresaId }: { empresaId: string }) {
  const qc = useQueryClient();
  const puedeMutar = useCanMutate();
  const [adding, setAdding] = useState(false);
  const [pick, setPick] = useState('');
  const [error, setError] = useState<string | null>(null);

  const asignados = useQuery({
    queryKey: ['empresa-usuarios', empresaId],
    queryFn: () => listUsuariosDeEmpresa(empresaId),
  });

  // Todos los USUARIO_EMPRESA, para ofrecer los que todavía no están en esta
  // organización. Se pide solo al abrir el selector.
  const todos = useQuery({
    queryKey: ['usuarios-emp-todos'],
    queryFn: () => listUsuarios({ rol: UserRole.USUARIO_EMPRESA }),
    enabled: adding,
  });

  const asignar = useMutation({
    mutationFn: (usuarioId: string) => asignarEmpresaAUsuario(usuarioId, empresaId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['empresa-usuarios', empresaId] });
      qc.invalidateQueries({ queryKey: ['usuarios'] });
      setPick('');
      setAdding(false);
      setError(null);
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      setError(err.response?.data?.error?.message ?? 'No se pudo asignar el usuario.');
    },
  });

  const quitar = useMutation({
    mutationFn: (usuarioId: string) => quitarEmpresaDeUsuario(usuarioId, empresaId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['empresa-usuarios', empresaId] });
      qc.invalidateQueries({ queryKey: ['usuarios'] });
    },
  });

  const idsActuales = new Set((asignados.data ?? []).map((u) => u.id));
  const noAsignados = (todos.data ?? []).filter((u) => !idsActuales.has(u.id));

  return (
    <div>
      <div className="rowBetween" style={{ marginBottom: 12 }}>
        <strong style={{ fontSize: 13 }}>
          {asignados.data?.length ?? 0} usuario(s) asignado(s)
        </strong>
        {puedeMutar && !adding && (
          <button type="button" className="btn btn-ghost" onClick={() => setAdding(true)}>
            <PlusIcon size={14} /> Asignar usuario
          </button>
        )}
      </div>

      {error && <p className="status status-error" style={{ marginBottom: 10 }}>{error}</p>}

      {adding && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            marginBottom: 14,
            flexWrap: 'wrap',
          }}
        >
          <select
            className="input"
            style={{ maxWidth: 320 }}
            value={pick}
            onChange={(e) => setPick(e.target.value)}
          >
            <option value="">— Elegí un usuario —</option>
            {noAsignados.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nombre ? `${u.nombre} · ${u.email}` : u.email}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!pick || asignar.isPending}
            onClick={() => asignar.mutate(pick)}
          >
            {asignar.isPending ? 'Asignando…' : 'Asignar'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setAdding(false)}>
            Cancelar
          </button>
          {todos.isLoading && <span className="text-element-mid">Cargando usuarios…</span>}
          {todos.data && noAsignados.length === 0 && (
            <span className="text-element-mid" style={{ fontSize: 12.5 }}>
              No quedan usuarios de empresa sin asignar.
            </span>
          )}
        </div>
      )}

      {asignados.isLoading && <p className="text-element-mid">Cargando usuarios…</p>}

      {asignados.data && asignados.data.length === 0 && (
        <p className="text-element-mid">
          Esta organización todavía no tiene usuarios asignados.
        </p>
      )}

      {asignados.data && asignados.data.length > 0 && (
        <div className="tableWrap">
          <table className="dataTable">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Email</th>
                <th>Estado</th>
                {puedeMutar && <th style={{ width: 60 }}></th>}
              </tr>
            </thead>
            <tbody>
              {asignados.data.map((u) => (
                <tr key={u.id}>
                  <td style={{ fontWeight: 600 }}>{u.nombre || '—'}</td>
                  <td>{u.email}</td>
                  <td>
                    {u.pendienteActivacion ? (
                      <span style={{ color: '#b45309' }}>Pendiente de activación</span>
                    ) : u.activo ? (
                      <span style={{ color: '#166534' }}>Activo</span>
                    ) : (
                      <span className="text-element-mid">Inactivo</span>
                    )}
                  </td>
                  {puedeMutar && (
                    <td>
                      <button
                        type="button"
                        className="btn btn-link"
                        style={{ color: 'var(--color-error-dark)', padding: 0 }}
                        title="Quitar de esta organización"
                        disabled={quitar.isPending}
                        onClick={() => quitar.mutate(u.id)}
                      >
                        <TrashIcon size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
